/**
 * Quais combinações o terminal fica, e quais o navegador leva.
 *
 * O xterm já sabe gerar a sequência de escape de quase tudo — `Alt+Backspace`
 * vira `ESC DEL`, `Ctrl+U` vira `0x15`. O que atrapalha não é o xterm, é o
 * navegador interceptar a combinação ANTES, para a própria função dele:
 * `Ctrl+R` recarrega, `Ctrl+L` foca a barra de endereço, `Ctrl+D` marca
 * favorito, `Ctrl+F` abre a busca da página.
 *
 * Sem isto, quem está dentro de um shell perde os atalhos que usa o dia
 * inteiro e a página faz outra coisa no lugar — o pior tipo de surpresa,
 * porque a ação errada acontece de verdade.
 *
 * ## O que NÃO dá para resgatar numa aba
 *
 * `Ctrl+W`, `Ctrl+T`, `Ctrl+N` e as variantes com Shift são reservadas pelo
 * Chrome e `preventDefault` não as alcança. `Ctrl+W` é a perda que dói —
 * apagar palavra. No app de desktop elas funcionam, porque o Electron captura
 * o teclado inteiro; é uma razão concreta para preferir o app quando se vive
 * dentro do shell.
 *
 * Preferi não fingir que resolvo isso: uma lista que promete `Ctrl+W` e não
 * entrega seria pior que a ausência.
 */

/** Combinações que o navegador reserva e NÃO libera, por mais que se peça. */
export const IMPOSSIVEIS_NA_ABA = ['Ctrl+W', 'Ctrl+T', 'Ctrl+N', 'Ctrl+Shift+T'] as const

/**
 * Teclas com Ctrl que o SHELL usa e o navegador rouba — e que dá para
 * recuperar. A letra é minúscula, como `KeyboardEvent.key` entrega.
 *
 * Fora da lista de proposito:
 *
 * - `c` e `v`: copiar e colar. Num terminal `Ctrl+C` e interrupcao, mas
 *   roubar o copiar do navegador quebraria o gesto que todo mundo usa para
 *   tirar texto da tela. O xterm ja resolve isso sozinho: com selecao ativa
 *   ele deixa o copiar passar, sem selecao manda o `0x03`.
 * - `x`: recortar, mesma razao.
 * - `+`, `-`, `0`: zoom da pagina. Ninguem espera que o terminal engula isso.
 */
const CTRL_DO_SHELL = new Set([
  'a', // inicio da linha
  'd', // EOF / apaga caractere a frente
  'e', // fim da linha
  'f', // avanca caractere (e a busca do navegador)
  'k', // apaga ate o fim da linha
  'l', // limpa a tela
  'n', // proxima da historia — mas o navegador leva Ctrl+N; fica aqui para
       // o app de desktop, onde funciona
  'p', // anterior da historia (e imprimir, no navegador)
  'r', // busca reversa na historia (e recarregar)
  's', // congela a saida (e salvar pagina)
  'u', // apaga a linha inteira
  'y', // cola o que foi apagado
])

export interface TeclaMinima {
  type: string
  ctrlKey: boolean
  altKey: boolean
  metaKey: boolean
  shiftKey: boolean
  key: string
}

/**
 * `true` = o terminal processa e o navegador não vê.
 *
 * Devolve a decisão sem tocar no evento, para poder ser testada sem DOM.
 */
export function ehAtalhoDoTerminal(ev: TeclaMinima): boolean {
  if (ev.type !== 'keydown') return false

  // Alt (Meta em terminal) + qualquer coisa é do shell: Alt+Backspace apaga
  // palavra, Alt+D apaga a próxima, Alt+B e Alt+F andam por palavra.
  // Alt+Tab é do sistema operacional e nunca chega aqui.
  if (ev.altKey && !ev.ctrlKey && !ev.metaKey) return true

  if (ev.ctrlKey && !ev.altKey && !ev.metaKey) {
    // Shift muda o significado no navegador (Ctrl+Shift+T reabre aba) e não
    // corresponde a atalho de shell — deixa passar.
    if (ev.shiftKey) return false
    return CTRL_DO_SHELL.has(ev.key.toLowerCase())
  }

  return false
}
